/**
 * Particle emitter for creating various particle effects
 */

import type { Particle, Shape } from '../utils/types';
import {
  PARTICLE_LIFE_MS,
  PARTICLE_BASE_SPEED,
  CLICK_PARTICLE_COUNT,
  CENTER_PARTICLE_COUNT,
} from '../utils/constants';

/**
 * Creates particles in a radial pattern from a point
 */
export function emitRadialParticles(
  x: number,
  y: number,
  count: number,
  color: string,
  speed: number = PARTICLE_BASE_SPEED
): Particle[] {
  const particles: Particle[] = [];

  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count;
    const velocity = speed * (0.5 + Math.random() * 0.5);

    particles.push({
      x,
      y,
      vx: Math.cos(angle) * velocity,
      vy: Math.sin(angle) * velocity,
      life: PARTICLE_LIFE_MS,
      maxLife: PARTICLE_LIFE_MS,
      color,
      size: 2 + Math.random() * 2,
    });
  }

  return particles;
}

/**
 * Creates particles for a click event
 */
export function emitClickParticles(x: number, y: number, color: string): Particle[] {
  return emitRadialParticles(x, y, CLICK_PARTICLE_COUNT, color, PARTICLE_BASE_SPEED);
}

/**
 * Creates particles for the true center of a shape
 */
export function emitCenterParticles(x: number, y: number, color: string): Particle[] {
  return emitRadialParticles(x, y, CENTER_PARTICLE_COUNT, color, PARTICLE_BASE_SPEED * 0.8);
}

/**
 * Creates particles that trace the outline of a shape
 */
export function emitShapeOutlineParticles(shape: Shape): Particle[] {
  const particles: Particle[] = [];
  const particleCount = Math.floor(shape.size * 2); // More particles for bigger shapes

  switch (shape.type) {
    case 'circle':
      particles.push(...emitCircleOutline(shape, particleCount));
      break;
    case 'square':
      particles.push(...emitSquareOutline(shape, particleCount));
      break;
    case 'rectangle':
      particles.push(...emitRectangleOutline(shape, particleCount));
      break;
    case 'triangle':
      particles.push(...emitTriangleOutline(shape, particleCount));
      break;
    case 'pentagon':
      particles.push(...emitPentagonOutline(shape, particleCount));
      break;
  }

  return particles;
}

function emitCircleOutline(shape: Shape, count: number): Particle[] {
  const particles: Particle[] = [];
  const radius = shape.size / 2;

  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + shape.rotation;
    const x = shape.x + Math.cos(angle) * radius;
    const y = shape.y + Math.sin(angle) * radius;

    // Particles explode outward
    const vx = Math.cos(angle) * PARTICLE_BASE_SPEED * 1.5;
    const vy = Math.sin(angle) * PARTICLE_BASE_SPEED * 1.5;

    particles.push({
      x,
      y,
      vx,
      vy,
      life: PARTICLE_LIFE_MS,
      maxLife: PARTICLE_LIFE_MS,
      color: shape.color,
      size: 2,
    });
  }

  return particles;
}

function emitSquareOutline(shape: Shape, count: number): Particle[] {
  const particles: Particle[] = [];
  const halfSize = shape.size / 2;
  const particlesPerSide = Math.floor(count / 4);

  const corners = [
    { x: -halfSize, y: -halfSize },
    { x: halfSize, y: -halfSize },
    { x: halfSize, y: halfSize },
    { x: -halfSize, y: halfSize },
  ];

  for (let side = 0; side < 4; side++) {
    const start = corners[side];
    const end = corners[(side + 1) % 4];

    for (let i = 0; i < particlesPerSide; i++) {
      const t = i / particlesPerSide;
      const localX = start.x + (end.x - start.x) * t;
      const localY = start.y + (end.y - start.y) * t;

      // Rotate around center
      const rotatedX = localX * Math.cos(shape.rotation) - localY * Math.sin(shape.rotation);
      const rotatedY = localX * Math.sin(shape.rotation) + localY * Math.cos(shape.rotation);

      const x = shape.x + rotatedX;
      const y = shape.y + rotatedY;

      // Explode outward from center
      const angle = Math.atan2(rotatedY, rotatedX);
      const vx = Math.cos(angle) * PARTICLE_BASE_SPEED * 1.5;
      const vy = Math.sin(angle) * PARTICLE_BASE_SPEED * 1.5;

      particles.push({
        x,
        y,
        vx,
        vy,
        life: PARTICLE_LIFE_MS,
        maxLife: PARTICLE_LIFE_MS,
        color: shape.color,
        size: 2,
      });
    }
  }

  return particles;
}

function emitRectangleOutline(shape: Shape, count: number): Particle[] {
  const particles: Particle[] = [];
  const width = shape.size * 1.5;
  const height = shape.size;
  const halfWidth = width / 2;
  const halfHeight = height / 2;

  const corners = [
    { x: -halfWidth, y: -halfHeight },
    { x: halfWidth, y: -halfHeight },
    { x: halfWidth, y: halfHeight },
    { x: -halfWidth, y: halfHeight },
  ];

  const perimeter = (width + height) * 2;
  const particlesPerUnit = count / perimeter;
  const particlesPerHSide = Math.floor(width * particlesPerUnit);
  const particlesPerVSide = Math.floor(height * particlesPerUnit);

  const sidesParticleCount = [particlesPerHSide, particlesPerVSide, particlesPerHSide, particlesPerVSide];

  for (let side = 0; side < 4; side++) {
    const start = corners[side];
    const end = corners[(side + 1) % 4];
    const particleCount = sidesParticleCount[side];

    for (let i = 0; i < particleCount; i++) {
      const t = i / particleCount;
      const localX = start.x + (end.x - start.x) * t;
      const localY = start.y + (end.y - start.y) * t;

      // Rotate around center
      const rotatedX = localX * Math.cos(shape.rotation) - localY * Math.sin(shape.rotation);
      const rotatedY = localX * Math.sin(shape.rotation) + localY * Math.cos(shape.rotation);

      const x = shape.x + rotatedX;
      const y = shape.y + rotatedY;

      // Explode outward from center
      const angle = Math.atan2(rotatedY, rotatedX);
      const vx = Math.cos(angle) * PARTICLE_BASE_SPEED * 1.5;
      const vy = Math.sin(angle) * PARTICLE_BASE_SPEED * 1.5;

      particles.push({
        x,
        y,
        vx,
        vy,
        life: PARTICLE_LIFE_MS,
        maxLife: PARTICLE_LIFE_MS,
        color: shape.color,
        size: 2,
      });
    }
  }

  return particles;
}

function emitTriangleOutline(shape: Shape, count: number): Particle[] {
  const particles: Particle[] = [];
  const radius = shape.size / 2;
  const particlesPerSide = Math.floor(count / 3);

  const vertices = [
    { angle: -Math.PI / 2, x: 0, y: -radius },
    { angle: Math.PI / 6, x: radius * Math.cos(Math.PI / 6), y: radius * Math.sin(Math.PI / 6) },
    { angle: (5 * Math.PI) / 6, x: radius * Math.cos((5 * Math.PI) / 6), y: radius * Math.sin((5 * Math.PI) / 6) },
  ];

  for (let side = 0; side < 3; side++) {
    const start = vertices[side];
    const end = vertices[(side + 1) % 3];

    for (let i = 0; i < particlesPerSide; i++) {
      const t = i / particlesPerSide;
      const localX = start.x + (end.x - start.x) * t;
      const localY = start.y + (end.y - start.y) * t;

      // Rotate around center
      const rotatedX = localX * Math.cos(shape.rotation) - localY * Math.sin(shape.rotation);
      const rotatedY = localX * Math.sin(shape.rotation) + localY * Math.cos(shape.rotation);

      const x = shape.x + rotatedX;
      const y = shape.y + rotatedY;

      // Explode outward from center
      const angle = Math.atan2(rotatedY, rotatedX);
      const vx = Math.cos(angle) * PARTICLE_BASE_SPEED * 1.5;
      const vy = Math.sin(angle) * PARTICLE_BASE_SPEED * 1.5;

      particles.push({
        x,
        y,
        vx,
        vy,
        life: PARTICLE_LIFE_MS,
        maxLife: PARTICLE_LIFE_MS,
        color: shape.color,
        size: 2,
      });
    }
  }

  return particles;
}

function emitPentagonOutline(shape: Shape, count: number): Particle[] {
  const particles: Particle[] = [];
  const radius = shape.size / 2;
  const particlesPerSide = Math.floor(count / 5);

  const vertices = [];
  for (let i = 0; i < 5; i++) {
    const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2;
    vertices.push({
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
    });
  }

  for (let side = 0; side < 5; side++) {
    const start = vertices[side];
    const end = vertices[(side + 1) % 5];

    for (let i = 0; i < particlesPerSide; i++) {
      const t = i / particlesPerSide;
      const localX = start.x + (end.x - start.x) * t;
      const localY = start.y + (end.y - start.y) * t;

      // Rotate around center
      const rotatedX = localX * Math.cos(shape.rotation) - localY * Math.sin(shape.rotation);
      const rotatedY = localX * Math.sin(shape.rotation) + localY * Math.cos(shape.rotation);

      const x = shape.x + rotatedX;
      const y = shape.y + rotatedY;

      // Explode outward from center
      const angle = Math.atan2(rotatedY, rotatedX);
      const vx = Math.cos(angle) * PARTICLE_BASE_SPEED * 1.5;
      const vy = Math.sin(angle) * PARTICLE_BASE_SPEED * 1.5;

      particles.push({
        x,
        y,
        vx,
        vy,
        life: PARTICLE_LIFE_MS,
        maxLife: PARTICLE_LIFE_MS,
        color: shape.color,
        size: 2,
      });
    }
  }

  return particles;
}

